import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FaPlus, FaSave, FaTrash, FaSync, FaArrowUp, FaArrowDown, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import api from '../../../api/apiClient';

const initialAwardState = {
  id: null,
  title: '',
  issuer: '',
  year: '',
  icon: '',
  description_html: '',
  is_active: true,
  is_highlighted: false
};

const AwardDescriptionEditor = ({ value, onChange, disabled }) => {
  const isUpdatingFromProps = useRef(false);
  
  const editor = useEditor(
    {
      extensions: [StarterKit],
      content: value || '',
      editable: !disabled,
      onUpdate({ editor }) {
        if (isUpdatingFromProps.current) return;
        const html = editor.getHTML();
        onChange(html);
      }
    },
    []
  );

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      isUpdatingFromProps.current = true;
      editor.commands.setContent(value || '', false);
      setTimeout(() => {
        isUpdatingFromProps.current = false;
      }, 0);
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div className="border rounded p-2 bg-gray-50 text-gray-500 text-sm">Loading editor...</div>
    );
  }

  return (
    <div className="border rounded" style={{ minHeight: '160px' }}>
      <EditorContent editor={editor} className="p-2" />
    </div>
  );
};

const AwardManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [awards, setAwards] = useState([]);
  const [awardForm, setAwardForm] = useState(initialAwardState);
  const [selectedAwardId, setSelectedAwardId] = useState(null);

  const [savingAward, setSavingAward] = useState(false);

  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterHighlighted, setFilterHighlighted] = useState('all');

  const normalize = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const loadAwards = useCallback(async () => {
    const params = {};
    if (search && search.trim()) params.search = search.trim();
    if (filterActive === 'active') params.is_active = '1';
    if (filterActive === 'inactive') params.is_active = '0';
    if (filterHighlighted === 'highlighted') params.is_highlighted = '1';
    if (filterHighlighted === 'non-highlighted') params.is_highlighted = '0';

    const response = await api.awards.list(params);
    if (response.data?.success) {
      setAwards(response.data.data || []);
    }
  }, [search, filterActive, filterHighlighted]);

  const fetchAll = useCallback(async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) setLoading(true);
      await loadAwards();
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [loadAwards]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchAll({ showLoader: false });
    } finally {
      setRefreshing(false);
    }
  }, [fetchAll, loading, refreshing]);

  const sortedAwards = useMemo(() => {
    return [...awards].sort((a, b) => a.position - b.position || new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  }, [awards]);

  const handleAwardFormChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setAwardForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleAwardDescriptionChange = useCallback((html) => {
    setAwardForm((prev) => ({
      ...prev,
      description_html: html || ''
    }));
  }, []);

  const handleNewAward = useCallback(() => {
    setSelectedAwardId(null);
    setAwardForm(initialAwardState);
  }, []);

  const handleEditAward = useCallback((award) => {
    if (!award) return;
    setSelectedAwardId(award.id);
    setAwardForm({
      id: award.id,
      title: award.title || '',
      issuer: award.issuer || '',
      year: award.year || '',
      icon: award.icon || '',
      description_html: award.description_html || '',
      is_active: !!award.is_active,
      is_highlighted: !!award.is_highlighted
    });
  }, []);

  const handleSaveAward = useCallback(async (event) => {
    event.preventDefault();

    const title = normalize(awardForm.title);
    const description_html = awardForm.description_html || '';
    if (!title || !description_html.trim()) return;

    setSavingAward(true);
    try {
      const payload = {
        title,
        issuer: normalize(awardForm.issuer) || null,
        year: normalize(awardForm.year) || null,
        icon: normalize(awardForm.icon) || null,
        description_html,
        is_active: !!awardForm.is_active,
        is_highlighted: !!awardForm.is_highlighted
      };

      if (awardForm.id) {
        await api.awards.update(awardForm.id, payload);
      } else {
        await api.awards.create(payload);
      }

      await loadAwards();
      handleNewAward();
    } finally {
      setSavingAward(false);
    }
  }, [awardForm, handleNewAward, loadAwards, normalize]);

  const handleDeleteAward = useCallback(async (awardId) => {
    if (!awardId) return;
    await api.awards.remove(awardId);
    if (selectedAwardId === awardId) {
      handleNewAward();
    }
    await loadAwards();
  }, [handleNewAward, loadAwards, selectedAwardId]);

  const changeAwardOrder = useCallback(async (awardId, direction) => {
    const items = sortedAwards.map((a) => ({ id: a.id, position: a.position }));
    const index = items.findIndex((x) => x.id === awardId);
    if (index === -1) return;

    if (direction === 'up' && index > 0) {
      const tmp = items[index - 1].position;
      items[index - 1].position = items[index].position;
      items[index].position = tmp;
    }
    if (direction === 'down' && index < items.length - 1) {
      const tmp = items[index + 1].position;
      items[index + 1].position = items[index].position;
      items[index].position = tmp;
    }

    await api.awards.reorder(items);
    await loadAwards();
  }, [loadAwards, sortedAwards]);

  useEffect(() => {
    loadAwards();
  }, [loadAwards]);

  return (
    <div className="p-3 sm:p-6 md:p-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/site-management')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h3 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Awards Management</h3>
            <p className="text-sm text-gray-600 mt-1">Manage awards and recognitions with issuer, year and rich descriptions.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            {refreshing ? 'Refreshing...' : (<><FaSync className="inline-block mr-2"/> Refresh</>)}
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleNewAward}
            disabled={savingAward}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            <FaPlus className="inline-block mr-2" /> New Award
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-5">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Search</label>
            <div className="flex items-center bg-white border border-purple-200 rounded-lg px-3 py-2">
              <FaSearch className="text-gray-400 mr-2" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, issuer, year or description..." className="w-full outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Status</label>
            <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)} className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm">
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Highlight</label>
            <select value={filterHighlighted} onChange={(e) => setFilterHighlighted(e.target.value)} className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm">
              <option value="all">All</option>
              <option value="highlighted">Highlighted only</option>
              <option value="non-highlighted">Non-highlighted only</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h6 className="text-lg font-bold mb-3">Awards</h6>
          {loading ? (
            <div className="text-center py-6"><div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div></div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
              <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="text-left py-3 px-4">Title</th>
                    <th className="text-left py-3 px-4">Issuer</th>
                    <th className="text-center py-3 px-4" style={{ width: '80px' }}>Year</th>
                    <th className="text-center py-3 px-4" style={{ width: '110px' }}>Highlight</th>
                    <th className="text-center py-3 px-4" style={{ width: '100px' }}>Status</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-purple-50">
                  {sortedAwards.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-gray-500 py-6">No awards found. Use the editor below to create your first award.</td>
                    </tr>
                  ) : (
                    sortedAwards.map((award, index) => (
                      <tr key={award.id}>
                        <td className="py-3 px-4 align-top">
                          <button onClick={() => handleEditAward(award)} className="text-left text-sm font-medium text-purple-800 hover:underline">{award.title}</button>
                        </td>
                        <td className="py-3 px-4 align-top text-sm text-gray-600">{award.issuer || '-'}</td>
                        <td className="py-3 px-3 text-left text-sm">{award.year || '-'}</td>
                        <td className="py-3 px-3 text-left">
                          {award.is_highlighted ? (
                            <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">Highlighted</span>
                          ) : (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">Normal</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-left">
                          {award.is_active ? (
                            <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold">Active</span>
                          ) : (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">Inactive</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="d-flex gap-1 justify-content-end">
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeAwardOrder(award.id, 'up')}
                              disabled={index === 0}
                              title="Move Up"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowUp className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeAwardOrder(award.id, 'down')}
                              disabled={index === sortedAwards.length - 1}
                              title="Move Down"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowDown className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleDeleteAward(award.id)}
                              title="Delete Award"
                              className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                            >
                              <FaTrash className="w-5 h-5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t">
          <h6 className="text-lg font-bold mb-3">{selectedAwardId ? 'Edit Award' : 'New Award'}</h6>
          <form onSubmit={handleSaveAward} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input type="text" name="title" value={awardForm.title} onChange={handleAwardFormChange} disabled={savingAward} placeholder="Award title" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Issuer (optional)</label>
                <input type="text" name="issuer" value={awardForm.issuer} onChange={handleAwardFormChange} disabled={savingAward} placeholder="Who issued the award" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Year (optional)</label>
                <input type="text" name="year" value={awardForm.year} onChange={handleAwardFormChange} disabled={savingAward} placeholder="e.g. 2024" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Icon (optional)</label>
                <input type="text" name="icon" value={awardForm.icon} onChange={handleAwardFormChange} disabled={savingAward} placeholder="e.g. fa-trophy or custom key" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
                <p className="text-xs text-gray-500 mt-1">This is a key used by your frontend to render an icon (no upload).</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="is_active" checked={awardForm.is_active} onChange={handleAwardFormChange} disabled={savingAward} className="w-4 h-4" />
                <span>Active</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="is_highlighted" checked={awardForm.is_highlighted} onChange={handleAwardFormChange} disabled={savingAward} className="w-4 h-4" />
                <span>Highlighted</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <AwardDescriptionEditor value={awardForm.description_html} onChange={handleAwardDescriptionChange} disabled={savingAward} />
              <p className="text-xs text-gray-500 mt-2">Use this rich text editor to describe the award. HTML is stored and rendered on the public view.</p>
            </div>

            <div className="flex justify-end items-center gap-3">
              {selectedAwardId && (
                <Button
                  bsPrefix="custom-hamburger"
                  type="button"
                  onClick={handleNewAward}
                  disabled={savingAward}
                  className="border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2"
                >
                  Cancel
                </Button>
              )}
              <Button
                bsPrefix="custom-hamburger"
                type="submit"
                disabled={savingAward}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2 flex items-center gap-2"
              >
                {savingAward ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Save Award
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AwardManager;
